import React, { memo, useEffect, useState, useMemo } from 'react';
import { useAtom } from 'jotai';
import styled from 'styled-components';
import { Rnd } from 'react-rnd';
import { Button, Tooltip, Input, Tag, message } from 'antd';
import {
  CloseOutlined,
  EnvironmentOutlined,
  CopyOutlined,
  SearchOutlined,
  DatabaseOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { MapDataLayerPopupProps, mapDataLayerPopupPropsAtom } from '../store/MapDataLayerAtom';
import MapUtils from '../../utils/MapUtils';

let setMapDataLayerPopupProps: (props: MapDataLayerPopupProps | null) => void;

export const showMapDataLayerPopup = (props: MapDataLayerPopupProps | null) => {
  if (setMapDataLayerPopupProps) {
    setMapDataLayerPopupProps(props);
  }
};

const MapDataLayerPopup: React.FC = () => {
  const [props, setProps] = useAtom(mapDataLayerPopupPropsAtom);
  setMapDataLayerPopupProps = setProps;

  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [searchText, setSearchText] = useState<string>('');

  const lng = props?.lngLat?.[0];
  const lat = props?.lngLat?.[1];
  const hasCoords = typeof lng === 'number' && typeof lat === 'number';

  // Calculate initial position near clicked point on map
  useEffect(() => {
    if (!props?.lngLat || !window.map) {
      if (props && !position) {
        setPosition({
          x: Math.max(20, window.innerWidth - 380),
          y: 90,
        });
      }
      return;
    }

    try {
      const point = window.map.project({ lng: props.lngLat[0], lat: props.lngLat[1] });
      const panelWidth = 350;
      const panelHeight = 440;
      let x = point.x + 20;
      let y = point.y - 80;

      if (x + panelWidth > window.innerWidth - 20) {
        x = Math.max(20, point.x - panelWidth - 20);
      }
      if (y + panelHeight > window.innerHeight - 20) {
        y = Math.max(70, window.innerHeight - panelHeight - 20);
      }
      if (y < 70) y = 70;

      setPosition({ x, y });
    } catch {
      setPosition({
        x: Math.max(20, window.innerWidth - 380),
        y: 90,
      });
    }
  }, [props?.lngLat, props?.title]);

  // Reset search text when opening new feature
  useEffect(() => {
    setSearchText('');
  }, [props]);

  const handleClose = () => {
    setProps(null);
    MapUtils.clearHighlight();
  };

  const handleCopyCoords = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!hasCoords || lat == null || lng == null) return;
    const text = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    navigator.clipboard
      .writeText(text)
      .then(() => {
        message.success('Đã sao chép tọa độ vào clipboard');
      })
      .catch(() => {
        message.error('Không thể sao chép tọa độ');
      });
  };

  const handleCopyAllProperties = () => {
    if (!props?.properties) return;
    try {
      const json = JSON.stringify(props.properties, null, 2);
      navigator.clipboard.writeText(json).then(() => {
        message.success('Đã sao chép toàn bộ thuộc tính (JSON)');
      });
    } catch {
      message.error('Không thể sao chép thuộc tính');
    }
  };

  // Filtered properties
  const propertyEntries = useMemo(() => {
    if (!props?.properties || typeof props.properties !== 'object') {
      return [];
    }
    const entries = Object.entries(props.properties);
    if (!searchText.trim()) {
      return entries;
    }
    const query = searchText.toLowerCase();
    return entries.filter(([key, value]) => {
      const keyMatch = key.toLowerCase().includes(query);
      const valMatch = String(value ?? '').toLowerCase().includes(query);
      return keyMatch || valMatch;
    });
  }, [props?.properties, searchText]);

  const totalPropertiesCount = props?.properties ? Object.keys(props.properties).length : 0;

  const renderValue = (val: any) => {
    if (val === null || val === undefined || val === '') {
      return <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>—</span>;
    }

    if (typeof val === 'boolean') {
      return val ? (
        <Tag color="blue" style={{ margin: 0, fontSize: 11, lineHeight: '18px' }}>true</Tag>
      ) : (
        <Tag color="default" style={{ margin: 0, fontSize: 11, lineHeight: '18px' }}>false</Tag>
      );
    }

    const strVal = String(val);

    // Color hex check
    if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(strVal)) {
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{
              width: 12,
              height: 12,
              borderRadius: 2,
              backgroundColor: strVal,
              border: '1px solid #cbd5e1',
              display: 'inline-block',
            }}
          />
          <code>{strVal}</code>
        </span>
      );
    }

    // URL check
    if (strVal.startsWith('http://') || strVal.startsWith('https://')) {
      return (
        <a href={strVal} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>
          {strVal.length > 30 ? strVal.substring(0, 30) + '...' : strVal}
        </a>
      );
    }

    if (typeof val === 'object') {
      try {
        return <code>{JSON.stringify(val)}</code>;
      } catch {
        return strVal;
      }
    }

    return strVal;
  };

  if (!props || !props.lngLat) return null;

  const badgeText = props.sourceLayer || 'Dữ liệu bản đồ';
  const displayTitle = props.title || 'Đối tượng bản đồ';

  return (
    <Rnd
      position={position || { x: Math.max(20, window.innerWidth - 380), y: 90 }}
      onDragStop={(_e, d) => {
        setPosition({ x: d.x, y: d.y });
      }}
      dragHandleClassName="draggable-header"
      enableResizing={false}
      bounds="window"
      style={{ zIndex: 1000 }}
    >
      <UI_PanelWrapper>
        {/* Draggable Header */}
        <UI_Header className="draggable-header">
          <UI_HeaderLeft>
            <UI_Badge title={`Lớp: ${badgeText}`}>
              <DatabaseOutlined style={{ marginRight: 4, fontSize: 11 }} />
              <span>{badgeText}</span>
            </UI_Badge>
            <UI_Title title={displayTitle}>{displayTitle}</UI_Title>
          </UI_HeaderLeft>
          <UI_HeaderRight>
            <UI_CloseButton onClick={handleClose} title="Đóng panel">
              <CloseOutlined style={{ fontSize: 12 }} />
            </UI_CloseButton>
          </UI_HeaderRight>
        </UI_Header>

        {/* Panel Body */}
        <UI_Body>
          {/* Coordinates Section */}
          {hasCoords && lat != null && lng != null && (
            <UI_CoordinatesRow>
              <UI_CoordinatesLabel>
                <EnvironmentOutlined style={{ color: '#ef4444', fontSize: 13 }} />
                <span>Tọa độ</span>
              </UI_CoordinatesLabel>
              <UI_CoordinatesValue>
                <span>
                  {lat.toFixed(6)}, {lng.toFixed(6)}
                </span>
                <Tooltip title="Sao chép tọa độ">
                  <Button
                    type="text"
                    size="small"
                    icon={<CopyOutlined style={{ fontSize: 12, color: '#64748b' }} />}
                    style={{ padding: '0 2px', height: 'auto', lineHeight: 1 }}
                    onClick={handleCopyCoords}
                  />
                </Tooltip>
              </UI_CoordinatesValue>
            </UI_CoordinatesRow>
          )}

          {/* Properties Section */}
          <UI_PropertiesSection>
            <UI_SectionHeader>
              <UI_SectionTitle>
                Thuộc tính
                {totalPropertiesCount > 0 && (
                  <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 4 }}>
                    ({propertyEntries.length === totalPropertiesCount
                      ? totalPropertiesCount
                      : `${propertyEntries.length}/${totalPropertiesCount}`})
                  </span>
                )}
              </UI_SectionTitle>

              {totalPropertiesCount > 0 && (
                <Tooltip title="Sao chép JSON toàn bộ thuộc tính">
                  <Button
                    type="text"
                    size="small"
                    icon={<CopyOutlined style={{ fontSize: 11, color: '#64748b' }} />}
                    style={{ fontSize: 11, height: 22, padding: '0 6px', color: '#64748b' }}
                    onClick={handleCopyAllProperties}
                  >
                    JSON
                  </Button>
                </Tooltip>
              )}
            </UI_SectionHeader>

            {/* Search Input for Properties (if > 4 properties) */}
            {totalPropertiesCount > 4 && (
              <UI_SearchWrapper>
                <Input
                  size="small"
                  placeholder="Lọc thuộc tính..."
                  prefix={<SearchOutlined style={{ color: '#94a3b8', fontSize: 12 }} />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  allowClear
                />
              </UI_SearchWrapper>
            )}

            {/* Properties List */}
            <UI_Properties_Wrapper>
              {propertyEntries.length > 0 ? (
                propertyEntries.map(([key, value], index) => (
                  <UI_Properties_Row key={key || index}>
                    <UI_Properties_Label title={key}>
                      <span>{key}</span>
                    </UI_Properties_Label>
                    <UI_Properties_Value>{renderValue(value)}</UI_Properties_Value>
                  </UI_Properties_Row>
                ))
              ) : (
                <UI_EmptyState>
                  <FileTextOutlined style={{ fontSize: 18, color: '#cbd5e1', marginBottom: 4 }} />
                  <div>
                    {searchText
                      ? 'Không có thuộc tính phù hợp'
                      : 'Không có dữ liệu thuộc tính'}
                  </div>
                </UI_EmptyState>
              )}
            </UI_Properties_Wrapper>
          </UI_PropertiesSection>
        </UI_Body>
      </UI_PanelWrapper>
    </Rnd>
  );
};

export default memo(MapDataLayerPopup);

/* ── Styled Components ─────────────────────────────────────────────────────── */

const UI_PanelWrapper = styled.div`
  width: 350px;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`;

const UI_Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  user-select: none;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;

const UI_HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
`;

const UI_HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const UI_CloseButton = styled.div`
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  &:hover {
    background-color: #fee2e2;
    color: #ef4444;
  }
`;

const UI_Badge = styled.div`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  max-width: 140px;
  padding: 3px 8px;
  background: #eff6ff;
  color: #2563eb;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  user-select: none;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const UI_Title = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UI_Body = styled.div`
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: calc(85vh - 80px);
  overflow-y: auto;
`;

const UI_CoordinatesRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 10px;
  background: #f8fafc;
  border: 1px solid #f1f5f9;
  border-radius: 6px;
`;

const UI_CoordinatesLabel = styled.div`
  font-size: 12px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
`;

const UI_CoordinatesValue = styled.div`
  font-size: 12px;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
  font-family: monospace;
`;

const UI_PropertiesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const UI_SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const UI_SectionTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const UI_SearchWrapper = styled.div`
  margin-bottom: 2px;
`;

const UI_Properties_Wrapper = styled.div`
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid #f1f5f9;
  border-radius: 6px;
  padding: 4px 8px;
  background: #f8fafc;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 2px;
  }
`;

const UI_Properties_Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 6px 4px;
  border-bottom: 1px solid #f1f5f9;
  gap: 12px;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #f1f5f9;
    border-radius: 4px;
  }
`;

const UI_Properties_Label = styled.div`
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
  min-width: 90px;
  max-width: 140px;
  word-break: break-word;
  line-height: 1.3;
`;

const UI_Properties_Value = styled.div`
  font-size: 12px;
  color: #1e293b;
  text-align: right;
  word-break: break-word;
  line-height: 1.3;
  flex: 1;
`;

const UI_EmptyState = styled.div`
  padding: 24px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #94a3b8;
  text-align: center;
`;